
import { db } from './firebase';
import { 
  collection, 
  addDoc, 
  query, 
  orderBy, 
  limit, 
  onSnapshot, 
  QuerySnapshot, 
  DocumentData,
  doc,
  setDoc,
  getDoc,
  collectionGroup,
  where
} from 'firebase/firestore';
import { WorkoutSession, UserProfile, LeaderboardEntry } from '../types';

export const saveWorkoutToHistory = async (userId: string, session: WorkoutSession, userProfile?: UserProfile | null) => {
  // Safety check: Do not attempt to write if guest
  if (!userId || userId === 'guest') {
    console.warn("Attempted to save workout for guest user to Firestore. Ignoring.");
    return;
  }
  
  try {
    const workoutData = {
      ...session,
      userId,
      timestamp: Date.now(),
      nickname: userProfile?.nickname || 'Unknown',
      photoURL: userProfile?.photoURL || null,
    };

    const workoutsRef = collection(db, 'users', userId, 'workouts');
    await addDoc(workoutsRef, workoutData);
    console.log("Workout saved to Firestore");
  } catch (e) {
    console.error("Error adding workout to history: ", e);
    throw e;
  }
};

export const subscribeToWorkoutHistory = (userId: string, callback: (data: WorkoutSession[]) => void) => {
  if (!userId || userId === 'guest') return () => {};

  try {
    const workoutsRef = collection(db, 'users', userId, 'workouts');
    const q = query(workoutsRef, orderBy('date', 'desc'), limit(50));
    
    const unsubscribe = onSnapshot(q, (querySnapshot: QuerySnapshot<DocumentData>) => {
      const workouts: WorkoutSession[] = [];
      querySnapshot.forEach((doc) => {
        workouts.push(doc.data() as WorkoutSession);
      });
      callback(workouts);
    }, (error) => {
      console.error("Error listening to workout history: ", error);
      callback([]); 
    });

    return unsubscribe;
  } catch (e) {
    console.error("Error setting up subscription: ", e);
    return () => {};
  }
};

export const saveUserProfile = async (userId: string, profile: UserProfile) => {
  if (!userId || userId === 'guest') return;
  
  try {
    const userRef = doc(db, 'users', userId);
    await setDoc(userRef, profile, { merge: true });
  } catch (e) {
    console.error("Error saving profile: ", e);
    throw e;
  }
};

// New helper to verify data actually exists
export const getUserProfile = async (userId: string): Promise<UserProfile | null> => {
  if (!userId) return null;
  try {
    const userRef = doc(db, 'users', userId);
    const docSnap = await getDoc(userRef);
    if (docSnap.exists()) {
      return docSnap.data() as UserProfile;
    }
    return null;
  } catch (e) {
    console.error("Error reading profile:", e);
    throw e;
  }
};

export const subscribeToLeaderboard = (
  exercise: string, 
  duration: number, 
  callback: (data: LeaderboardEntry[]) => void
) => {
  try {
    const q = query(
      collectionGroup(db, 'workouts'),
      where('exercise', '==', exercise),
      where('mode', '==', 'TIME_ATTACK'),
      where('targetDuration', '==', duration),
      orderBy('reps', 'desc'),
      limit(20)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const entries: LeaderboardEntry[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        entries.push({
          userId: data.userId,
          nickname: data.nickname || 'Unknown Operator',
          photoURL: data.photoURL,
          date: data.date,
          reps: data.reps,
          accuracy: data.accuracyScore,
          exercise: data.exercise,
          duration: data.durationSeconds
        });
      });
      callback(entries);
    }, (error) => {
       console.error("Leaderboard Error:", error);
       callback([]);
    });

    return unsubscribe;
  } catch (e) {
    console.error("Error in leaderboard subscription:", e);
    return () => {};
  }
};
